import {Alert, Divider, LinearProgress, MenuItem, TextField, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import React from 'react';
import {CreditRequestClassificationEnum} from '../../../model/credit/credit-request-classification.enum';
import {CreditRequestModel} from '../../../model/credit/credit-request.model';
import {CreditRequestStatusEnum} from '../../../model/credit/credit-request-status.enum';
import {isPreDebitStatus, useCreditRequestDrawerState} from '../state/credit-request-drawer-state';
import {CreditRequestUtils} from '../utils/credit-request.utils';

type Props = {
  request: CreditRequestModel
  isDrawerOpen: boolean
  refreshRequests: () => Promise<void>
  onDrawerClosed: () => void
  translate: (key: string) => string
  translateLocal: (key: string) => string
}

const CLASSIFICATION_OPTIONS: {value: CreditRequestClassificationEnum, label: string}[] = [
  {value: CreditRequestClassificationEnum.IMPROVEMENT, label: 'Usprawnienie'},
  {value: CreditRequestClassificationEnum.BUG_CRITICAL_ERROR, label: 'Błąd krytyczny (SLA)'},
  {value: CreditRequestClassificationEnum.BUG_MAJOR_ERROR, label: 'Błąd poważny (SLA)'},
  {value: CreditRequestClassificationEnum.BUG_OTHER_ERROR, label: 'Inny błąd (SLA)'},
  {value: CreditRequestClassificationEnum.BUG_UPDATE_ERROR, label: 'Błąd po aktualizacji (SLA)'},
]

export const CreditRequestDrawer = ({isDrawerOpen, onDrawerClosed, request, refreshRequests, translate, translateLocal}: Props) => {
  const state = useCreditRequestDrawerState(request, refreshRequests)

  const r = state.request
  const balance = state.account?.balanceHours ?? 0
  const Icon = CreditRequestUtils.getStatusIcon(r.status)

  const buildHeader = () =>
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Icon style={{color: CreditRequestUtils.getStatusIconColor(r.status)}}/>
        <Typography variant="h6">{translate(`credit-support.status.${r.status}`)}</Typography>
        <Box ml="auto">
          <Typography variant="caption">ID: {r.id}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{mt: 1}}>
        {r.organizationName ?? ''} / {r.accountName ?? ''}
      </Typography>
      <Typography variant="body2">
        {translateLocal('drawer.balance')}: <b>{balance.toFixed(1)} h</b>
      </Typography>
    </Box>

  const buildDescription = () =>
    <Box>
      <Typography variant="h6">{r.title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {translate(`credit-support.module.${r.module}`)} · {translate(`credit-support.priority.${r.priority}`)}
      </Typography>
      <Typography variant="body2" sx={{mt: 1, whiteSpace: 'pre-wrap'}}>{r.description}</Typography>
      <Box mt={1}>
        <Typography variant="caption">
          {translateLocal('drawer.requireEstimate')}: {r.requestEstimateBeforeStart ? '✓' : '—'}
          {' '}|{' '}
          {translateLocal('drawer.requireAcceptance')}: {r.requireEstimateAcceptance ? '✓' : '—'}
        </Typography>
      </Box>
      {r.classification && (
        <Box mt={1}>
          <Typography variant="caption">
            {translateLocal('drawer.classification')}: <b>{CLASSIFICATION_OPTIONS.find(o => o.value === r.classification)?.label ?? r.classification}</b>
          </Typography>
          {r.proposedSolution && (
            <Typography variant="body2" sx={{mt: 0.5, whiteSpace: 'pre-wrap'}}>
              {translateLocal('drawer.proposedSolution')}: {r.proposedSolution}
            </Typography>
          )}
        </Box>
      )}
    </Box>

  const buildEstimateSection = () => {
    if (r.status !== CreditRequestStatusEnum.NEEDS_ESTIMATION && r.status !== CreditRequestStatusEnum.ESTIMATE_REJECTED) return null
    return <Box>
      <Typography variant="subtitle1">{translateLocal('drawer.estimate')}</Typography>
      <Box display="flex" gap={2} alignItems="flex-start" mt={1} flexWrap="wrap">
        <TextField
          size="small"
          type="number"
          inputProps={{step: 0.5, min: 0.5}}
          label={translateLocal('drawer.hours')}
          value={state.estimateHours}
          onChange={(e) => state.setEstimateHours(e.target.value)}
        />
        <TextField
          size="small"
          type="number"
          inputProps={{step: 1, min: 1}}
          label={translateLocal('drawer.deliveryDays')}
          value={state.deliveryDays}
          onChange={(e) => state.setDeliveryDays(e.target.value)}
          sx={{width: 140}}
        />
        <TextField
          select
          size="small"
          label={translateLocal('drawer.classification')}
          value={state.classification}
          onChange={(e) => state.setClassification(e.target.value as CreditRequestClassificationEnum)}
          sx={{width: 220}}
        >
          {CLASSIFICATION_OPTIONS.map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
      </Box>
      <TextField
        size="small"
        multiline
        minRows={2}
        label={translateLocal('drawer.proposedSolution')}
        value={state.proposedSolution}
        onChange={(e) => state.setProposedSolution(e.target.value)}
        sx={{mt: 1, width: '100%'}}
      />
      <Box mt={1}>
        <Button
          variant="contained"
          disabled={!state.estimateValid}
          onClick={state.submitEstimate}
        >
          {translateLocal('drawer.button.estimate')}
        </Button>
      </Box>
      {
        state.insufficientBalance &&
        <Alert severity="warning" sx={{mt: 1}}>
          {translateLocal('warning.insufficient-balance')}
          {' '}
          ({translateLocal('drawer.balance')}: {balance.toFixed(1)} h,
          {' '}{translateLocal('drawer.deficit')}: {state.balanceDeficit.toFixed(1)} h)
        </Alert>
      }
    </Box>
  }

  const buildAcceptanceSection = () => {
    if (r.status !== CreditRequestStatusEnum.WAITING_CUSTOMER_ACCEPTANCE) return null
    return <Box>
      <Typography variant="subtitle1">{translateLocal('drawer.customerAcceptance')}</Typography>
      <Typography variant="body2">
        {translateLocal('drawer.estimatedHours')}: <b>{r.hoursEstimated?.toFixed(1)} h</b>
      </Typography>
      {r.deliveryDays && (
        <Typography variant="body2">
          {translateLocal('drawer.deliveryDays')}: <b>{r.deliveryDays} dni</b>
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {translateLocal('drawer.acceptanceHint')}
      </Typography>
    </Box>
  }

  const buildScheduledSection = () => {
    if (r.status !== CreditRequestStatusEnum.SCHEDULED) return null
    return <Box>
      <Typography variant="subtitle1">{translateLocal('drawer.scheduled')}</Typography>
      <Typography variant="body2">
        {translateLocal('drawer.estimatedHours')}: <b>{r.hoursEstimated?.toFixed(1)} h</b>
      </Typography>
      {r.plannedDeliveryDate && (
        <Typography variant="body2">
          {translateLocal('drawer.plannedDelivery')}: <b>{r.plannedDeliveryDate}</b>
        </Typography>
      )}
      {r.acceptedAt && (
        <Typography variant="body2">
          {translateLocal('drawer.acceptedAt')}: <b>{r.acceptedAt}</b>
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {translateLocal('drawer.scheduledHint')}
      </Typography>
      <Box mt={1}>
        <Button variant="contained" onClick={state.startWork}>
          {translateLocal('drawer.button.start')}
        </Button>
      </Box>
    </Box>
  }

  const buildProgressSection = () => {
    if (r.status !== CreditRequestStatusEnum.IN_PROGRESS && r.status !== CreditRequestStatusEnum.PAUSED) return null
    const estimated = r.hoursEstimated ?? 0
    const pct = estimated > 0 ? Math.min(100, (r.actualHoursSpent / estimated) * 100) : 0
    return <Box>
      <Typography variant="subtitle1">{translateLocal('drawer.progress')}</Typography>
      <Typography variant="body2">
        {r.actualHoursSpent.toFixed(1)} h / {estimated.toFixed(1)} h
      </Typography>
      <LinearProgress variant="determinate" value={pct} sx={{mt: 1, mb: 2}}/>
      <Box display="flex" gap={2}>
        <TextField
          size="small"
          type="number"
          inputProps={{step: 0.5, min: 0}}
          label={translateLocal('drawer.actualHours')}
          value={state.actualHours}
          onChange={(e) => state.setActualHours(e.target.value)}
        />
        <Button
          variant="outlined"
          disabled={!state.actualHoursValid}
          onClick={state.saveActualHours}
        >
          {translateLocal('drawer.button.saveActual')}
        </Button>
        {
          r.status === CreditRequestStatusEnum.IN_PROGRESS
            ? <Button variant="outlined" onClick={state.pauseRequest}>{translateLocal('drawer.button.pause')}</Button>
            : <Button variant="outlined" onClick={state.resumeRequest}>{translateLocal('drawer.button.resume')}</Button>
        }
      </Box>
    </Box>
  }

  const buildResolveSection = () => {
    if (r.status !== CreditRequestStatusEnum.IN_PROGRESS) return null
    return <Box>
      <Typography variant="subtitle1">{translateLocal('drawer.resolve')}</Typography>
      <TextField
        size="small"
        type="number"
        inputProps={{step: 0.5, min: 0}}
        label={translateLocal('drawer.actualHoursFinal')}
        value={state.actualHours}
        onChange={(e) => state.setActualHours(e.target.value)}
        sx={{mt: 1, mr: 2}}
      />
      <TextField
        size="small"
        multiline
        minRows={2}
        label={translateLocal('drawer.resolutionText')}
        value={state.resolutionText}
        onChange={(e) => state.setResolutionText(e.target.value)}
        sx={{mt: 1, width: '60%'}}
      />
      <Box mt={1}>
        <Button
          variant="contained"
          color="success"
          disabled={!state.actualHoursValid || state.resolutionText.trim().length === 0}
          onClick={state.resolveRequest}
        >
          {translateLocal('drawer.button.resolve')}
        </Button>
      </Box>
    </Box>
  }

  const buildFooter = () => <Box mt="auto" pt={2} display="flex" gap={2}>
    <Button variant="outlined" onClick={onDrawerClosed} sx={{flex: 1}}>
      {translate('common.cancel.title')}
    </Button>
    {
      isPreDebitStatus(r.status) &&
      <Button variant="outlined" color="error" onClick={state.cancelRequest}>
        {translateLocal('drawer.button.cancel')}
      </Button>
    }
  </Box>

  return <Drawer
    anchor="right"
    open={isDrawerOpen}
    onClose={onDrawerClosed}
    PaperProps={{
      sx: {
        width: '50%',
        marginTop: '64px',
        height: 'calc(100% - 64px)',
        position: 'fixed',
        right: 0,
        padding: 3,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }
    }}
  >
    {buildHeader()}
    <Divider sx={{my: 2}}/>
    {buildDescription()}
    {buildEstimateSection() && <Divider sx={{my: 2}}/>}
    {buildEstimateSection()}
    {buildAcceptanceSection() && <Divider sx={{my: 2}}/>}
    {buildAcceptanceSection()}
    {buildScheduledSection() && <Divider sx={{my: 2}}/>}
    {buildScheduledSection()}
    {buildProgressSection() && <Divider sx={{my: 2}}/>}
    {buildProgressSection()}
    {buildResolveSection() && <Divider sx={{my: 2}}/>}
    {buildResolveSection()}
    {buildFooter()}
  </Drawer>
}
